import React from 'react';
import { useTranslation } from 'react-i18next';
import { BillingPeriod } from '../../../models/functions/billing-period';
import { cn } from '../../../utils/tailwind';
import { getPriceComparison } from './pricing-data';
import styles from './homepage-pricing.module.css';

export const SavingsBadge: React.FC<{ period: BillingPeriod; withVat: boolean }> = ({ period, withVat }) => {
    const { t, i18n } = useTranslation();
    return (
        <div className="pointer-events-none absolute z-[3] -top-[1.5rem] left-[1.6rem] right-[1.6rem] sm:left-[3.2rem] sm:right-[3.2rem]">
            {(['halfyearly', 'yearly'] as const).map((offerPeriod) => {
                const comparison = getPriceComparison(offerPeriod, withVat);
                const visible = period === offerPeriod && comparison.savings > 0;
                const amount = new Intl.NumberFormat(i18n.language === 'en' ? 'en-US' : 'cs-CZ', {
                    style: 'currency',
                    currency: 'CZK',
                    maximumFractionDigits: 0,
                }).format(comparison.savings);
                const hasFreeMonths = offerPeriod === 'yearly' && comparison.freeMonths >= 2;
                const badgeClass =
                    'inline-flex shrink-0 items-center gap-[0.4rem] whitespace-nowrap rounded-full px-[0.8rem] py-[0.6rem] text-[1.1rem] font-bold leading-[1.8rem] text-neutral-white min-[360px]:px-[1rem] min-[360px]:text-[1.2rem] sm:px-[1.4rem] sm:text-[1.3rem]';
                return (
                    <span
                        key={offerPeriod}
                        aria-hidden={!visible}
                        data-visible={visible}
                        className={cn(styles.savingsBadge, 'absolute left-0 inline-flex max-w-full gap-[0.6rem]')}
                    >
                        <span
                            className={cn(badgeClass, offerPeriod === 'yearly' ? 'bg-brand-primary' : 'bg-[#e65c00]')}
                        >
                            {hasFreeMonths && (
                                <svg
                                    aria-hidden="true"
                                    viewBox="0 0 16 16"
                                    className="h-[1.2rem] w-[1.2rem] shrink-0"
                                    fill="currentColor"
                                >
                                    <path d="m8 0 2.1 5.9L16 8l-5.9 2.1L8 16l-2.1-5.9L0 8l5.9-2.1Z" />
                                </svg>
                            )}
                            {hasFreeMonths
                                ? t('pages.homepage.pricingDesign.twoMonthsFree')
                                : t('pages.homepage.pricingDesign.savings', { amount })}
                        </span>
                        {hasFreeMonths && (
                            <span className={cn(badgeClass, 'bg-[#e65c00]')}>
                                {t('pages.homepage.pricingDesign.savings', { amount })}
                            </span>
                        )}
                    </span>
                );
            })}
        </div>
    );
};

const reactionParticles = {
    halfyearly: [
        { id: 'left', emoji: '👍' },
        { id: 'center', emoji: '👍' },
        { id: 'right', emoji: '👍' },
    ],
    yearly: [
        { id: 'left', emoji: '💜' },
        { id: 'center', emoji: '😍' },
        { id: 'right', emoji: '💜' },
        { id: 'far-left', emoji: '💜' },
        { id: 'far-right', emoji: '💜' },
    ],
};

export const PricingReaction: React.FC<{ period: 'halfyearly' | 'yearly' }> = ({ period }) => (
    <div aria-hidden="true" className={styles.reaction}>
        {reactionParticles[period].map(({ id, emoji }) => (
            <span key={id} className={styles.reactionParticle}>
                {emoji}
            </span>
        ))}
    </div>
);
